/*
 * FILE: unified_ui/src/lib/op.ts
 * PURPOSE: OpenPanel web analytics instance — real browser-side visitors, sessions
 *          and screen views for the SPA, UNIFIED with the backend observe sink by
 *          using the SAME profileId key (unified account userId, fallback to the
 *          platform userId) that botCore's openpanelSink resolves for this human.
 * OWNS: OpenPanel SDK configuration + identify/clear helpers.
 * EXPORTS: op, identifyOpenPanelUser, clearOpenPanelUser, openPanelChannel
 *
 * Config (Vite env):
 *   VITE_OPENPANEL_CLIENT_ID — OpenPanel project client id (UUIDv4). Empty/missing
 *                              → analytics disabled (op === null, helpers no-op).
 *   VITE_OPENPANEL_API_URL   — self-hosted OpenPanel API base URL.
 *                              Defaults to https://monitor.cyberholding.ru
 *
 * Identity note: @openpanel/web keeps profileId in memory only — every page load
 * must call identifyOpenPanelUser() after the auth session resolves, so queued
 * screen views flush onto the unified profile (waitForProfile: true).
 */
import { OpenPanel } from '@openpanel/web'

export type OpenPanelUser = {
  userId: number | string
  email?: string | null
  name?: string | null
  username?: string | null
}

const clientId = (import.meta.env.VITE_OPENPANEL_CLIENT_ID as string | undefined)?.trim()
const apiUrl =
  ((import.meta.env.VITE_OPENPANEL_API_URL as string | undefined)?.trim() ||
    'https://monitor.cyberholding.ru')

/**
 * Browser analytics instance. `waitForProfile: true` queues screen views until
 * identify() lands (auth is an async roundtrip), then flushes them onto the
 * unified profile — so even the very first pageview belongs to the human.
 */
export const op: OpenPanel | null = clientId
  ? new OpenPanel({
      clientId,
      apiUrl,
      trackScreenViews: true,
      trackOutgoingLinks: true,
      waitForProfile: true,
    })
  : null

/**
 * Channel label matching the backend observe envelope's channel for the SPA
 * context: telegram mini-app → tg-app, max mini-app → max-app, plain web → web-app.
 */
export function openPanelChannel(platform: string): string {
  if (platform === 'telegram') return 'tg-app'
  if (platform === 'max') return 'max-app'
  return 'web-app'
}

function splitName(name: string): { firstName?: string; lastName?: string } {
  const parts = name.trim().split(/\s+/)
  const firstName = parts[0]
  const lastName = parts.length > 1 ? parts.slice(1).join(' ') : undefined
  return firstName ? { firstName, lastName } : {}
}

/**
 * Associate the current browser session with the unified human — the SAME
 * profileId botCore's OpenPanel sink uses (String(user.userId) ?? platformUserId),
 * so messenger events, backend web events and browser screen views all land on
 * ONE profile. Also links the profile to the channel group (web-app/tg-app/max-app),
 * matching the backend sink's group semantics.
 */
export function identifyOpenPanelUser(
  user: OpenPanelUser | null,
  platformUserId: string,
  channel: string,
): void {
  if (!op) return
  const profileId = user?.userId !== undefined && user?.userId !== null
    ? String(user.userId)
    : platformUserId
  if (!profileId) return

  op.identify({
    profileId,
    ...(user?.email ? { email: user.email } : {}),
    ...(user?.name ? splitName(user.name) : {}),
    properties: {
      ...(user?.username ? { username: user.username } : {}),
      ...(user?.name ? { name: user.name } : {}),
      channel,
    },
  })
  op.setGroup(channel)
}

/** Forget the current profile (logout / session expiry) — subsequent events stay anonymous. */
export function clearOpenPanelUser(): void {
  if (!op) return
  op.clear()
}